import type { CustomerDeliveryDraftV1 } from "@lifescapsule/contracts";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../../shared/api";
import { CustomerButton, CustomerTextInput } from "../../shared/CustomerControls";
import { Icon } from "../../shared/Icon";
import {
  orderHref,
  orderReference,
  printHelpHref,
  type ReadingSelection,
} from "../next-ui/print/print-links";
import { DELIVERY_DRAFTS_ENABLED } from "./order-capabilities";
import {
  confirmPhysicalOrderAddress,
  draftConflict,
  getDeliveryDraft,
  getOriginalOrderInitiation,
  getPhysicalOrderStatus,
  initiatePhysicalOrder,
  printingPaused,
  printOrderRefusal,
  PRINTING_PAUSED_MESSAGE,
  saveDeliveryDraft,
  submitPhysicalOrder,
  type DeliveryAddressInput,
} from "./physical-order-api";
import { originalRequestOutcome, sessionSubject } from "./use-physical-order";
import "./print-order-experience.css";

/** Where the journey is: the page around it may title itself from this. */
export type PrintJourneyStage = "address" | "check" | "unknown" | "sent";

type Problem = { title: string; text: string; tone: "rejected" | "paused" | "unconfirmed" };

const emptyAddress: DeliveryAddressInput = {
  recipientName: "",
  line1: "",
  line2: "",
  city: "",
  region: "",
  postalCode: "",
  country: "US",
  phone: "",
};

/** No answer, a timeout or a server fault: the request may or may not have been recorded. */
function unconfirmed(cause: unknown) {
  return (
    !(cause instanceof ApiError) ||
    cause.status >= 500 ||
    cause.status === 408 ||
    cause.status === 429
  );
}

/** The service is still settling an earlier address confirmation: nothing is sent meanwhile. */
const addressStillSettling = new Error("The original address confirmation is still settling.");

/** States in which the order has gone past waiting for the customer's send. */
const sentStates = new Set([
  "SUBMISSION_QUEUED",
  "SUBMITTING",
  "RECONCILIATION_REQUIRED",
  "VALIDATING",
  "ACCEPTED",
  "IN_PRODUCTION",
  "PRINTED",
  "PARTIALLY_SHIPPED",
  "SHIPPED",
  "IN_TRANSIT",
  "DELIVERED",
]);

/** A saved draft's address as the form holds it. */
function draftAddressInput(
  address: NonNullable<CustomerDeliveryDraftV1["address"]>,
): DeliveryAddressInput {
  return { ...emptyAddress, ...address, line2: address.line2 ?? "", phone: address.phone ?? "" };
}

/** A draft the service still keeps: one that has expired is never resumed. */
function unexpired(draft: CustomerDeliveryDraftV1 | null) {
  return draft && Date.parse(draft.expiresAt) > Date.now() ? draft : null;
}

const draftDay = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" });

/** How long a saved draft is kept, from the service's own expiry (accepted R77 wording). */
function keptFor(draft: CustomerDeliveryDraftV1) {
  const until = draftDay.format(new Date(draft.expiresAt));
  const days = Math.round((Date.parse(draft.expiresAt) - Date.now()) / 86_400_000);
  return days >= 2 ? `Saved for ${days} days, until ${until}.` : `Saved until ${until}.`;
}

const DRAFT_BOUNDARY = "A saved draft isn’t an order and isn’t sent to the printer.";

/** What the page says about the draft: kept (saved, resumed, cleared) or a problem with it. */
type DraftNotice = {
  tone: "kept" | "problem";
  text: string;
  /** After a conflict: the details another tab or device saved, if it saved an address. */
  newer?: CustomerDeliveryDraftV1 | null;
};

/** One account's draft of one book, as last read or written. */
type DraftHeld = {
  scope: string;
  state: "loading" | "ready" | "unavailable";
  draft: CustomerDeliveryDraftV1 | null;
  notice?: DraftNotice;
};

/**
 * The included printed copy: where it goes, then a separate final check, then one saved request
 * (accepted R77 delivery, final check and receipt). Every request keeps its first idempotency key,
 * so a retry after a lost answer is the same request, never a second order; while an answer is
 * unknown nothing else can be changed, and the service's journal of the original request, then the
 * saved order, is looked up before anything is sent again. Saving the request is not the printer's
 * acceptance, and the page never says it is.
 *
 * With delivery drafts on (G2), the address can be saved for later and is resumed on the next
 * visit; a saved draft is private convenience, never an order or a confirmed address.
 */
export function PrintOrderJourney({
  bookId,
  bookRevision,
  previewId,
  entitlementId,
  replacementOfPhysicalOrderId,
  idToken,
  onOrderSent,
  onStageChange,
  bookTitle,
  returnTo,
  readingPageNumber,
  readingEdition,
  deliveryDrafts,
}: {
  bookId: string;
  bookRevision: number;
  previewId: string;
  entitlementId: string;
  replacementOfPhysicalOrderId?: string;
  idToken: string;
  /** Told once the request is saved, so the page can remember the order. */
  onOrderSent?: (physicalOrderId: string) => void;
  onStageChange?: (stage: PrintJourneyStage) => void;
  /** The book's title as the reader's list gives it, for the final check and receipt. */
  bookTitle?: string;
  /** A checked way back to this book, when the page has one. */
  returnTo?: { to: string; label: string };
  /** The reading page the order should remember for its own way back. */
  readingPageNumber?: number;
  readingEdition?: ReadingSelection;
  /**
   * Resume, save and clear the book's private delivery draft (G2). Defaults to the app-wide
   * switch, which stays off until account erasure covers drafts; tests pass it explicitly.
   */
  deliveryDrafts?: boolean;
}) {
  const [address, setAddress] = useState<DeliveryAddressInput>(emptyAddress);
  const [physicalOrderId, setPhysicalOrderId] = useState<string>();
  const [order, setOrder] = useState<{ quantity: number; included: number }>();
  const [stage, setStage] = useState<PrintJourneyStage>("address");
  const [busy, setBusy] = useState<"" | "review" | "submit" | "check" | "save" | "clear">("");
  const [problem, setProblem] = useState<Problem | null>(null);
  // The address the server confirmed: the final check shows exactly this, nothing typed since.
  const [confirmed, setConfirmed] = useState<{
    fingerprint: string;
    address: DeliveryAddressInput;
  }>();
  // An answer that never arrived holds everything else until it is known.
  const [unresolved, setUnresolved] = useState<"" | "initiation" | "submission">("");
  // What the saved request allows after a lost answer: sending the same request again
  // (available), waiting while the service settles it (pending), a fresh review because it was
  // refused before dispatch (rejected), help only (unavailable), or not yet looked up (unchecked).
  const [resend, setResend] = useState<
    "" | "available" | "pending" | "rejected" | "unavailable" | "unchecked"
  >("");
  const keys = useRef<{
    initiation?: { fingerprint: string; key: string };
    address?: { fingerprint: string; key: string };
    submission?: { fingerprint: string; key: string };
  }>({});
  // The address confirmation whose answer never came (or is still being settled): the service's
  // journal is read for it before any address is confirmed again.
  const addressUnsettled = useRef<{ physicalOrderId: string; key: string } | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const firstStage = useRef(true);
  const drafts = deliveryDrafts ?? DELIVERY_DRAFTS_ENABLED;
  // One account's draft of one book: an answer for another account or book is dropped.
  const draftScope = drafts ? `${sessionSubject(idToken)}\u0000${bookId}` : "";
  const liveDraftScope = useRef(draftScope);
  useLayoutEffect(() => {
    liveDraftScope.current = draftScope;
    return () => {
      liveDraftScope.current = "";
    };
  }, [draftScope]);
  const [held, setHeld] = useState<DraftHeld>();
  const draftHere = drafts && held?.scope === draftScope ? held : undefined;
  const draftLoading = drafts && (!draftHere || draftHere.state === "loading");
  const tokenRef = useRef(idToken);
  tokenRef.current = idToken;
  // Whose saved details fill the form, so another account's never stay in it.
  const resumedScope = useRef("");
  const deliveryAddress = useMemo<DeliveryAddressInput>(
    () => ({
      recipientName: address.recipientName,
      line1: address.line1,
      ...(address.line2?.trim() ? { line2: address.line2 } : {}),
      city: address.city,
      region: address.region,
      postalCode: address.postalCode,
      country: "US",
      ...(address.phone?.trim() ? { phone: address.phone } : {}),
    }),
    [address],
  );
  const fingerprint = JSON.stringify({
    bookId,
    bookRevision,
    previewId,
    entitlementId,
    replacementOfPhysicalOrderId,
    deliveryAddress,
  });
  const stageListener = useRef(onStageChange);
  stageListener.current = onStageChange;
  useEffect(() => {
    stageListener.current?.(stage);
    if (firstStage.current) {
      firstStage.current = false;
      return;
    }
    heading.current?.focus({ preventScroll: true });
    heading.current?.scrollIntoView?.({ block: "nearest", behavior: "instant" });
  }, [stage]);

  useEffect(() => {
    if (!draftScope) return;
    const asked = draftScope;
    if (resumedScope.current && resumedScope.current !== asked) {
      setAddress(emptyAddress);
      resumedScope.current = "";
    }
    setHeld({ scope: asked, state: "loading", draft: null });
    getDeliveryDraft(tokenRef.current, bookId)
      .then((found) => {
        if (liveDraftScope.current !== asked) return;
        const kept = unexpired(found);
        if (kept?.address) {
          setAddress(draftAddressInput(kept.address));
          resumedScope.current = asked;
        }
        setHeld({
          scope: asked,
          state: "ready",
          draft: kept,
          notice: kept?.address
            ? { tone: "kept", text: `We kept the delivery details you saved. ${keptFor(kept)}` }
            : undefined,
        });
      })
      .catch(() => {
        if (liveDraftScope.current !== asked) return;
        setHeld({
          scope: asked,
          state: "unavailable",
          draft: null,
          notice: {
            tone: "problem",
            text: "We couldn’t open your saved delivery details. You can enter them here; nothing was ordered.",
          },
        });
      });
  }, [draftScope, bookId]);

  function update<K extends keyof DeliveryAddressInput>(key: K, value: DeliveryAddressInput[K]) {
    if (unresolved || draftLoading) return;
    setAddress((current) => ({ ...current, [key]: value }));
    // "Saved" no longer describes what's typed once it changes.
    if (draftHere?.notice?.tone === "kept") setHeld({ ...draftHere, notice: undefined });
  }

  /**
   * The service's own key for this book's order, carried by a draft of this exact edition: an
   * order begun from it on an earlier visit is found again rather than begun twice. A corrected
   * sandbox print starts its own order, so it never takes the original's key.
   */
  function draftInitiationKey() {
    const draft = draftHere?.state === "ready" ? draftHere.draft : null;
    if (!draft || replacementOfPhysicalOrderId) return undefined;
    return draft.previewId === previewId &&
      draft.entitlementId === entitlementId &&
      draft.sourceRevision === bookRevision
      ? draft.initiationRequestKey
      : undefined;
  }

  /**
   * After a definite refusal the next try is a new request: a fresh delivery confirmation (and
   * quote) and a new send, never a replay of the refused ones. It stays this one order.
   */
  function forgetSentKeys() {
    delete keys.current.address;
    delete keys.current.submission;
  }

  /** Saves (or, with null, clears) the draft at the version last seen; a newer one wins. */
  async function writeDraft(next: DeliveryAddressInput | null) {
    if (!draftHere || draftHere.state === "loading") return;
    const asked = draftScope;
    const here = draftHere;
    setBusy(next ? "save" : "clear");
    try {
      const saved = await saveDeliveryDraft(idToken, bookId, {
        expectedVersion: here.draft?.version ?? null,
        previewId,
        entitlementId,
        sourceRevision: bookRevision,
        address: next,
      });
      if (liveDraftScope.current !== asked) return;
      if (!next) {
        setAddress(emptyAddress);
        resumedScope.current = "";
      } else resumedScope.current = asked;
      setHeld({
        scope: asked,
        state: "ready",
        draft: saved,
        notice: {
          tone: "kept",
          text: next
            ? `Delivery details saved. ${keptFor(saved)}`
            : "Your saved delivery details were removed. Nothing was ordered.",
        },
      });
    } catch (cause) {
      if (liveDraftScope.current !== asked) return;
      if (!draftConflict(cause)) {
        setHeld({
          ...here,
          notice: {
            tone: "problem",
            text: next
              ? "We couldn’t save your delivery details. They’re still here on this page; nothing was ordered."
              : "We couldn’t remove your saved delivery details. Please try again.",
          },
        });
        return;
      }
      // Saved first elsewhere: read the newer draft, and keep what was typed here on screen.
      let newer: CustomerDeliveryDraftV1 | null | undefined;
      try {
        newer = unexpired(await getDeliveryDraft(idToken, bookId));
      } catch {
        // Not read: its version stays unknown, and saving again asks once more.
        newer = undefined;
      }
      if (liveDraftScope.current !== asked) return;
      setHeld({
        scope: asked,
        state: "ready",
        draft: newer === undefined ? here.draft : newer,
        notice: {
          tone: "problem",
          text: `These delivery details were changed in another tab or on another device. What you typed is still here: save again to keep it${newer?.address ? ", or use the details saved there" : ""}.`,
          newer: newer ?? null,
        },
      });
    } finally {
      setBusy("");
    }
  }

  function saveForLater() {
    if (busy || unresolved) return;
    // The draft holds a complete address only: the form says what's missing.
    if (form.current && !form.current.reportValidity()) return;
    void writeDraft(deliveryAddress);
  }

  /** The first key issued for exactly this request; a different request gets its own. */
  function keyFor(kind: "initiation" | "address" | "submission", forFingerprint: string) {
    const issued = keys.current[kind];
    if (issued?.fingerprint === forFingerprint) return issued.key;
    const next = { fingerprint: forFingerprint, key: crypto.randomUUID() };
    keys.current[kind] = next;
    return next.key;
  }

  async function reviewDelivery(event: FormEvent) {
    event.preventDefault();
    if (busy || draftLoading) return;
    setBusy("review");
    setProblem(null);
    if (draftHere?.notice) setHeld({ ...draftHere, notice: undefined });
    const requestFingerprint = fingerprint;
    try {
      let orderId = physicalOrderId;
      const draftKey = orderId ? undefined : draftInitiationKey();
      if (!orderId && draftKey) {
        // Looked up before every start with the draft's key: never begun twice, and never begun
        // while it's unknown whether it already was.
        const receipt = await getOriginalOrderInitiation(idToken, bookId, draftKey).catch(() => {
          throw new Error("The original request couldn’t be looked up.");
        });
        if (receipt.receipt === "RECORDED" && receipt.physicalOrderId) {
          orderId = receipt.physicalOrderId;
          setPhysicalOrderId(orderId);
        }
      }
      if (!orderId) {
        try {
          const created = await initiatePhysicalOrder({
            idToken,
            bookId,
            previewId,
            expectedRevision: bookRevision,
            entitlementId,
            replacementOfPhysicalOrderId,
            address: deliveryAddress,
            idempotencyKey: draftKey ?? keyFor("initiation", requestFingerprint),
          });
          orderId = created.physicalOrder.physicalOrderId;
          // Kept as soon as it exists: a retried address confirmation updates this order rather
          // than claiming the included copy a second time.
          setPhysicalOrderId(orderId);
          const { quantity, includedCopyQuantity } = created.physicalOrder;
          setOrder(
            Number.isInteger(quantity) && Number.isInteger(includedCopyQuantity)
              ? { quantity, included: includedCopyQuantity }
              : undefined,
          );
          setUnresolved("");
        } catch (cause) {
          if (!printingPaused(cause) && unconfirmed(cause)) setUnresolved("initiation");
          throw cause;
        }
      }
      const addressFingerprint = `${orderId}:${requestFingerprint}`;
      const unsettled = addressUnsettled.current;
      if (unsettled?.physicalOrderId === orderId) {
        // Looked up before anything is sent again: a lost confirmation may have been recorded,
        // refused before dispatch, or still be settling.
        const outcome = await originalRequestOutcome(
          idToken,
          orderId,
          "ADDRESS",
          unsettled.key,
        ).catch(() => {
          throw new Error("The original address confirmation couldn’t be looked up.");
        });
        if (outcome === "UNKNOWN") throw addressStillSettling;
        addressUnsettled.current = null;
        if (outcome === "REJECTED") delete keys.current.address;
        if (
          outcome === "COMPLETED" &&
          keys.current.address?.key === unsettled.key &&
          keys.current.address.fingerprint === addressFingerprint
        ) {
          // That exact request (this address) was confirmed: nothing is sent again.
          setConfirmed({ fingerprint: requestFingerprint, address: deliveryAddress });
          setStage("check");
          return;
        }
        // Never seen, or a different address since: confirm it (same key when unchanged).
      }
      const addressKey = keyFor("address", addressFingerprint);
      try {
        await confirmPhysicalOrderAddress(idToken, orderId, deliveryAddress, addressKey);
      } catch (cause) {
        if (
          unconfirmed(cause) ||
          (cause instanceof ApiError && cause.code === "ORIGINAL_OPERATION_RECONCILIATION_REQUIRED")
        )
          addressUnsettled.current = { physicalOrderId: orderId, key: addressKey };
        // Refused before dispatch (a quote that expired, quoting paused, an address limit): the
        // next confirmation is a new request with its own key, never a replay of the refused one.
        else if (!printingPaused(cause)) delete keys.current.address;
        throw cause instanceof ApiError &&
          cause.code === "ORIGINAL_OPERATION_RECONCILIATION_REQUIRED"
          ? addressStillSettling
          : cause;
      }
      setConfirmed({ fingerprint: requestFingerprint, address: deliveryAddress });
      setStage("check");
    } catch (cause) {
      setProblem(
        printingPaused(cause)
          ? { title: "Printing is paused for now.", text: PRINTING_PAUSED_MESSAGE, tone: "paused" }
          : cause === addressStillSettling
            ? {
                title: "We’re still confirming your delivery details.",
                text: "Your first confirmation hasn’t settled yet, so it isn’t sent again. Try again in a moment: we check it before sending anything.",
                tone: "unconfirmed",
              }
            : printOrderRefusal(cause)
              ? {
                  title: "We can’t send this book to print yet.",
                  text: printOrderRefusal(cause)!,
                  tone: "rejected",
                }
              : cause instanceof ApiError && cause.code === "ORIGINAL_OPERATION_NOT_DISPATCHED"
                ? {
                    title: "We couldn’t confirm this delivery address yet.",
                    text: "The earlier confirmation was refused before anything was sent. Try again to confirm the address afresh; nothing was sent to print.",
                    tone: "rejected",
                  }
                : unconfirmed(cause)
                  ? {
                      title: "We couldn’t confirm your delivery details.",
                      text: "Your details are kept here. Try again: the same request is sent, so no second order is made.",
                      tone: "unconfirmed",
                    }
                  : {
                      title: "We couldn’t confirm this delivery address.",
                      text: "Check the address and try again. Nothing was sent to print.",
                      tone: "rejected",
                    },
      );
    } finally {
      setBusy("");
    }
  }

  async function sendToPrint() {
    if (busy || !physicalOrderId || !confirmed) return;
    setBusy("submit");
    setProblem(null);
    try {
      await submitPhysicalOrder(
        idToken,
        physicalOrderId,
        keyFor("submission", `${physicalOrderId}:${confirmed.fingerprint}`),
      );
      setUnresolved("");
      setResend("");
      setStage("sent");
      onOrderSent?.(physicalOrderId);
    } catch (cause) {
      if (printingPaused(cause))
        setProblem({
          title: "Printing is paused for now.",
          text: PRINTING_PAUSED_MESSAGE,
          tone: "paused",
        });
      else if (
        cause instanceof ApiError &&
        cause.code === "ORIGINAL_OPERATION_RECONCILIATION_REQUIRED"
      ) {
        // The service is still settling the original request: nothing may be sent again.
        setUnresolved("submission");
        setResend("pending");
        setStage("unknown");
      } else if (unconfirmed(cause)) {
        setUnresolved("submission");
        setResend("unchecked");
        setStage("unknown");
      } else if (printOrderRefusal(cause)) {
        // Refused by the order's own checks before anything was sent (A1): said as it is.
        forgetSentKeys();
        setProblem({
          title: "We can’t send this book to print yet.",
          text: printOrderRefusal(cause)!,
          tone: "rejected",
        });
      } else {
        forgetSentKeys();
        setProblem({
          title: "We couldn’t send this request.",
          text: "Nothing new was sent. Check the saved request before trying again; if the delivery quote has expired, review the address again.",
          tone: "rejected",
        });
      }
    } finally {
      setBusy("");
    }
  }

  /**
   * Reconciles a lost send, never sending anything itself: first the service's journal of the
   * original request (it alone can say this exact request was recorded, refused before dispatch,
   * or is still being settled), then the saved order's status. Only a request the journal hasn't
   * seen, on an order still waiting for it, may be sent again, and then with its original key.
   */
  async function checkSaved() {
    if (busy || !physicalOrderId) return;
    setBusy("check");
    setProblem(null);
    const sentWith = keys.current.submission?.key;
    try {
      const outcome = sentWith
        ? await originalRequestOutcome(idToken, physicalOrderId, "SUBMISSION", sentWith)
        : "NOT_OBSERVED";
      if (outcome === "COMPLETED") {
        setUnresolved("");
        setResend("");
        setStage("sent");
        onOrderSent?.(physicalOrderId);
        return;
      }
      if (outcome === "REJECTED") {
        forgetSentKeys();
        setResend("rejected");
        return;
      }
      const saved = await getPhysicalOrderStatus(idToken, physicalOrderId);
      if (saved.physicalOrderId !== physicalOrderId || saved.bookId !== bookId)
        throw new Error("The saved order belongs to another book.");
      if (sentStates.has(saved.state)) {
        setUnresolved("");
        setResend("");
        setStage("sent");
        onOrderSent?.(physicalOrderId);
      } else
        setResend(
          outcome === "UNKNOWN"
            ? "pending"
            : saved.state === "EVIDENCE_READY" && saved.submissionAvailable
              ? "available"
              : "unavailable",
        );
    } catch {
      setProblem({
        title: "We still couldn’t check your request.",
        text: "Your details are kept. Try again shortly, or ask us for help with the reference below.",
        tone: "unconfirmed",
      });
    } finally {
      setBusy("");
    }
  }

  const shown = confirmed?.address ?? deliveryAddress;
  const helpTo = printHelpHref(bookId, {
    orderId: physicalOrderId,
    page: readingPageNumber,
    selection: readingEdition,
  });
  const copies = order
    ? order.included >= order.quantity
      ? `${order.quantity} included`
      : String(order.quantity)
    : undefined;

  return (
    <section className="print-journey" aria-labelledby="print-journey-title" data-stage={stage}>
      {stage === "sent" && physicalOrderId ? (
        <div className="print-journey__receipt">
          <h2 id="print-journey-title" ref={heading} tabIndex={-1}>
            Request saved
          </h2>
          <p className="print-reference">
            Reference <strong>{orderReference(physicalOrderId)}</strong>
          </p>
          <dl className="print-final-facts">
            {bookTitle && (
              <div>
                <dt>Book</dt>
                <dd>{bookTitle}</dd>
              </div>
            )}
            {copies && (
              <div>
                <dt>Printed copies</dt>
                <dd>{copies}</dd>
              </div>
            )}
            {confirmed && (
              <div>
                <dt>Deliver to</dt>
                <dd>
                  <DeliveryLines address={confirmed.address} />
                </dd>
              </div>
            )}
          </dl>
          <section className="print-next">
            <h3>What happens next?</h3>
            <p>
              We check the book files, your included copy and the delivery details before the
              printer can accept your request. No printing or delivery date is confirmed yet.
            </p>
          </section>
          <div className="print-actions">
            <Link
              className="lc-action lc-action--primary lc-action--large"
              to={orderHref(physicalOrderId, readingPageNumber, readingEdition)}
            >
              <span>
                Follow this order
                <Icon name="arrow" size={18} />
              </span>
            </Link>
            {returnTo && <Link to={returnTo.to}>{returnTo.label}</Link>}
          </div>
        </div>
      ) : stage === "unknown" && physicalOrderId ? (
        <div className="print-uncertain">
          <Icon name="book" size={30} />
          <h2 id="print-journey-title" ref={heading} tabIndex={-1}>
            Check the original request.
          </h2>
          <p>
            {resend === "available"
              ? "We haven’t found this request saved yet. Sending it again repeats the same request, so if the first one did reach us, no second book is ordered."
              : resend === "pending"
                ? "We’re still confirming your original request with our printer. Please don’t send it again: check back in a few minutes. Your delivery details are safe."
                : resend === "rejected"
                  ? "Your original request wasn’t sent to the printer. Review your delivery details again before confirming; this stays the same order."
                  : resend === "unavailable"
                    ? "This request can’t be sent from here right now. Your delivery details are safe; ask us for help with the reference below."
                    : "Your delivery details are safe. Checking the saved request won’t send another book or create another request."}
          </p>
          <div className="print-actions">
            {resend === "rejected" ? (
              <CustomerButton
                size="large"
                onClick={() => {
                  setUnresolved("");
                  setResend("");
                  setStage("address");
                }}
              >
                Review delivery again
                <Icon name="arrow" size={18} />
              </CustomerButton>
            ) : resend === "available" ? (
              <CustomerButton
                size="large"
                busy={busy === "submit"}
                onClick={() => void sendToPrint()}
              >
                Send the same request again
                <Icon name="arrow" size={18} />
              </CustomerButton>
            ) : (
              <CustomerButton
                size="large"
                busy={busy === "check"}
                onClick={() => void checkSaved()}
                disabled={resend === "unavailable"}
              >
                Check saved request
                <Icon name="arrow" size={18} />
              </CustomerButton>
            )}
            <Link to={helpTo}>Ask for help with this request</Link>
          </div>
          <p className="print-note">Keep reference {orderReference(physicalOrderId)}.</p>
        </div>
      ) : stage === "check" && confirmed ? (
        <div className="print-final-check">
          <h2 id="print-journey-title" ref={heading} tabIndex={-1}>
            Ready for your final check.
          </h2>
          <div className="print-delivery-summary">
            <Icon name="check" size={23} />
            <div>
              <h3>Deliver to</h3>
              <address>
                <DeliveryLines address={shown} />
              </address>
            </div>
            <CustomerButton
              tone="quiet"
              disabled={Boolean(busy)}
              onClick={() => setStage("address")}
            >
              Edit details
            </CustomerButton>
          </div>
          <dl className="print-final-facts">
            {bookTitle && (
              <div>
                <dt>Your book</dt>
                <dd>{bookTitle}</dd>
              </div>
            )}
            <div>
              <dt>Version</dt>
              <dd>The exact files you approved</dd>
            </div>
            {copies && (
              <div>
                <dt>Printed copies</dt>
                <dd>{copies}</dd>
              </div>
            )}
          </dl>
          <p className="print-note">
            Your selection and address are saved together. A printer has not accepted this request.
          </p>
          <div className="print-actions">
            <CustomerButton
              size="large"
              busy={busy === "submit"}
              onClick={() => void sendToPrint()}
            >
              Confirm print request
              <Icon name="arrow" size={18} />
            </CustomerButton>
            {returnTo && <Link to={returnTo.to}>Return to this book</Link>}
          </div>
        </div>
      ) : (
        <form ref={form} className="print-address" onSubmit={(event) => void reviewDelivery(event)}>
          <h2 id="print-journey-title" ref={heading} tabIndex={-1}>
            Where should your book go?
          </h2>
          <p>
            Choose where your printed book should go. Deliver to a US address; you’ll check
            everything before confirming.
          </p>
          <fieldset className="print-field-grid" disabled={Boolean(unresolved) || draftLoading}>
            <legend className="sr-only">Delivery address</legend>
            <CustomerTextInput
              id="print-recipient-name"
              label="Recipient name"
              value={address.recipientName}
              onChange={(event) => update("recipientName", event.target.value)}
              autoComplete="name"
              maxLength={50}
              required
            />
            <CustomerTextInput
              id="print-address-line-1"
              label="Street address"
              value={address.line1}
              onChange={(event) => update("line1", event.target.value)}
              autoComplete="address-line1"
              maxLength={35}
              required
            />
            <CustomerTextInput
              id="print-address-line-2"
              label="Apartment, suite or unit"
              optional
              value={address.line2 ?? ""}
              onChange={(event) => update("line2", event.target.value)}
              autoComplete="address-line2"
              maxLength={35}
            />
            <div className="print-city-row">
              <CustomerTextInput
                id="print-city"
                label="City"
                value={address.city}
                onChange={(event) => update("city", event.target.value)}
                autoComplete="address-level2"
                maxLength={30}
                required
              />
              <CustomerTextInput
                id="print-region"
                label="State"
                description="Two-letter code"
                value={address.region}
                onChange={(event) => update("region", event.target.value.toUpperCase().slice(0, 2))}
                autoComplete="address-level1"
                pattern="[A-Z]{2}"
                minLength={2}
                maxLength={2}
                required
              />
              <CustomerTextInput
                id="print-postal-code"
                label="ZIP code"
                value={address.postalCode}
                onChange={(event) => update("postalCode", event.target.value)}
                autoComplete="postal-code"
                inputMode="numeric"
                pattern="[0-9]{5}(-[0-9]{4})?"
                maxLength={10}
                required
              />
            </div>
            <CustomerTextInput
              id="print-phone"
              label="Phone number"
              optional
              type="tel"
              value={address.phone ?? ""}
              onChange={(event) => update("phone", event.target.value)}
              autoComplete="tel"
              maxLength={25}
            />
          </fieldset>
          <div className="print-actions">
            <CustomerButton
              type="submit"
              size="large"
              busy={busy === "review"}
              disabled={draftLoading}
            >
              {unresolved ? "Try again" : "Review delivery"}
              <Icon name="arrow" size={18} />
            </CustomerButton>
            {drafts && !unresolved && (
              <CustomerButton
                type="button"
                tone="quiet"
                busy={busy === "save"}
                disabled={draftLoading || (Boolean(busy) && busy !== "save")}
                onClick={saveForLater}
              >
                Save for later
              </CustomerButton>
            )}
            {drafts && !unresolved && draftHere?.draft?.address && (
              <CustomerButton
                type="button"
                tone="quiet"
                busy={busy === "clear"}
                disabled={Boolean(busy) && busy !== "clear"}
                onClick={() => {
                  if (!busy) void writeDraft(null);
                }}
              >
                Forget saved details
              </CustomerButton>
            )}
          </div>
          {draftLoading && (
            <p className="print-note" role="status">
              Opening your saved delivery details…
            </p>
          )}
          {draftHere?.notice?.tone === "kept" && (
            <p className="print-saved" role="status">
              <Icon name="check" size={17} />
              {draftHere.notice.text}
            </p>
          )}
          {draftHere?.notice?.tone === "problem" && (
            <div className="print-error" role="alert">
              <p>{draftHere.notice.text}</p>
              {draftHere.notice.newer?.address && (
                <CustomerButton
                  type="button"
                  tone="secondary"
                  onClick={() => {
                    const saved = draftHere.notice?.newer?.address;
                    if (!saved) return;
                    setAddress(draftAddressInput(saved));
                    resumedScope.current = draftScope;
                    setHeld({ ...draftHere, notice: undefined });
                  }}
                >
                  Use the saved details
                </CustomerButton>
              )}
            </div>
          )}
          <p className="print-note">
            Nothing is sent until the separate final confirmation.
            {drafts && ` ${DRAFT_BOUNDARY}`}
          </p>
        </form>
      )}
      {problem && (
        <div className="print-error" role="alert">
          <strong>{problem.title}</strong>
          <p>{problem.text}</p>
          {physicalOrderId && stage !== "unknown" && problem.tone !== "paused" && (
            <Link to={orderHref(physicalOrderId, readingPageNumber, readingEdition)}>
              Check saved request
            </Link>
          )}
          {problem.tone !== "rejected" && <Link to={helpTo}>Ask for help</Link>}
        </div>
      )}
    </section>
  );
}

function DeliveryLines({ address }: { address: DeliveryAddressInput }) {
  return (
    <>
      {address.recipientName}
      <br />
      {address.line1}
      {address.line2 && (
        <>
          <br />
          {address.line2}
        </>
      )}
      <br />
      {address.city}, {address.region} {address.postalCode}
      <br />
      United States
      {address.phone && (
        <>
          <br />
          {address.phone}
        </>
      )}
    </>
  );
}
